import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layout/AdminLayout';
import { Plus, Copy, Loader2, Power, DollarSign, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAffiliates() {
  const [affs, setAffs] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', code: '', commission_pct: 15 });

  const load = async () => {
    setLoading(true);
    try {
      const [a, p] = await Promise.all([
        axios.get(`${API_URL}/api/admin/affiliates`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/affiliate-payouts`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setAffs(a.data || []);
      setPayouts(p.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load affiliates');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/admin/affiliates`, form, { withCredentials: true });
      toast.success('Affiliate created');
      setForm({ name: '', email: '', code: '', commission_pct: 15 });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create');
    } finally {
      setCreating(false);
    }
  };

  const copy = async (s) => { try { await navigator.clipboard.writeText(s); toast.success('Copied'); } catch { /* */ } };
  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this affiliate? Their share link will stop crediting them.')) return;
    try { await axios.post(`${API_URL}/api/admin/affiliates/${id}/deactivate`, {}, { withCredentials: true }); load(); } catch (err) { toast.error(err.response?.data?.detail); }
  };

  const markPaid = async (payoutId) => {
    try {
      await axios.post(`${API_URL}/api/admin/affiliate-payouts/${payoutId}/mark-paid`, {}, { withCredentials: true });
      toast.success('Payout marked paid');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not mark paid'); }
  };
  const rejectPayout = async (payoutId) => {
    try {
      await axios.post(`${API_URL}/api/admin/affiliate-payouts/${payoutId}/reject`, {}, { withCredentials: true });
      load();
    } catch (err) { toast.error(err.response?.data?.detail); }
  };

  return (
    <AdminLayout title="Affiliates">
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-[#0F172A] to-[#1D4ED8] text-white rounded-xl p-6">
          <h1 className="text-2xl font-bold mb-1" data-testid="admin-affiliates-title">Affiliates</h1>
          <p className="text-white/85 text-sm">Create affiliates, share their custom links, and approve payout requests. Affiliates log in to their own portal at <code className="bg-white/10 px-1 rounded">/affiliate-portal</code> via passwordless magic link.</p>
        </div>

        {/* Create */}
        <form onSubmit={create} className="bg-white border border-[#E2E8F0] rounded-xl p-5 grid md:grid-cols-5 gap-3" data-testid="affiliate-create-form">
          <input required placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm md:col-span-2" data-testid="aff-name" />
          <input required type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm md:col-span-2" data-testid="aff-email" />
          <input placeholder="Code (auto)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm" data-testid="aff-code" />
          <input type="number" step="0.1" placeholder="Commission %" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: parseFloat(e.target.value) })} className="px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm" data-testid="aff-pct" />
          <button type="submit" disabled={creating} className="btn-primary-blue md:col-span-4 inline-flex items-center justify-center gap-2" data-testid="aff-submit">
            {creating ? <><Loader2 size={14} className="animate-spin" /> Creating…</> : <><Plus size={14} /> Create affiliate</>}
          </button>
        </form>

        {/* Affiliates list */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#E2E8F0] flex items-center justify-between">
            <h2 className="font-bold text-[#0A0F1C]">Affiliates ({affs.length})</h2>
          </div>
          {loading ? (
            <div className="text-center py-10 text-[#94A3B8]"><Loader2 className="inline animate-spin mr-2" /> Loading…</div>
          ) : affs.length === 0 ? (
            <div className="text-center py-10 text-sm text-[#94A3B8]">No affiliates yet. Create your first one above.</div>
          ) : (
            <div className="divide-y divide-[#F1F5F9]">
              {affs.map((a) => (
                <div key={a.affiliate_id} className="p-4 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`aff-row-${a.code}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-[#0A0F1C]">{a.name}</span>
                      <span className="text-xs text-[#475569]">· {a.email}</span>
                      {!a.is_active && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-red-100 text-red-700">Inactive</span>}
                    </div>
                    <div className="text-xs text-[#475569] mt-1 flex items-center gap-2 flex-wrap">
                      <code className="bg-[#F8FAFC] border border-[#E2E8F0] px-2 py-0.5 rounded font-mono">{a.code}</code>
                      <span>{a.commission_pct}% commission</span>
                      <span>· {a.conversion_count} conversions</span>
                      <span>· ${(a.total_revenue || 0).toLocaleString()} revenue driven</span>
                      <span>· <strong className="text-emerald-700">${(a.total_commission || 0).toLocaleString()} earned</strong></span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => copy(a.share_url)} className="btn-outline text-xs inline-flex items-center gap-1.5"><Copy size={12} /> Copy Link</button>
                    {a.is_active && (
                      <button onClick={() => deactivate(a.affiliate_id)} className="text-red-600 hover:bg-red-50 p-2 rounded" title="Deactivate"><Power size={14} /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Payouts */}
        {payouts.length > 0 && (
          <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-[#E2E8F0]"><h2 className="font-bold text-[#0A0F1C]">Payout requests ({payouts.length})</h2></div>
            <div className="divide-y divide-[#F1F5F9]">
              {payouts.map((p) => (
                <div key={p.payout_id} className="p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`payout-row-${p.payout_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#0A0F1C]">{p.affiliate_name} · ${(p.amount || 0).toLocaleString()} via {p.method.toUpperCase()}</div>
                    <div className="text-xs text-[#475569] mt-0.5">Send to: <code className="bg-[#F1F5F9] px-1 rounded">{p.payout_to}</code> · requested {(p.requested_at || '').slice(0, 10)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${p.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : p.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{p.status}</span>
                    {p.status === 'pending' && (
                      <>
                        <button onClick={() => markPaid(p.payout_id)} className="text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded inline-flex items-center gap-1" data-testid={`mark-paid-${p.payout_id}`}><DollarSign size={12} /> Mark paid</button>
                        <button onClick={() => rejectPayout(p.payout_id)} className="text-xs font-bold border border-red-200 text-red-700 hover:bg-red-50 px-3 py-1.5 rounded inline-flex items-center gap-1"><Trash2 size={12} /> Reject</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
